import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {
  Driver_Read_Typeahead,
  getV1MessagingChannelsRecipientsTypeahead,
  GetV1MessagingChannelsRecipientsTypeaheadData,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { FieldValues } from 'react-hook-form';

import { DriverTypeahead } from '~hooks/useDrivers/models';
import { RecipientsQuery } from '~hooks/useMessaging/messagingRecipients';

import DriverOptionSecondary from '../Parts/DriverOptionSecondary';
import AsyncAutocompleteField from '../v2/Autocomplete/AsyncAutocompleteField';
import { CommonAsyncAutocompleteFieldProps } from '../v2/Autocomplete/types';

type Props<T extends FieldValues> = CommonAsyncAutocompleteFieldProps<
  T,
  Driver_Read_Typeahead,
  DriverTypeahead,
  GetV1MessagingChannelsRecipientsTypeaheadData
> & { ownCompanyId: string; recipientFilters?: RecipientsQuery };

// No company in the path: a company segment would rescope results away from what the send accepts.
export default function AutocompleteMessagingRecipientsTypeahead<T extends FieldValues>({
  ownCompanyId,
  recipientFilters,
  ...rest
}: Props<T>) {
  return (
    <AsyncAutocompleteField
      {...rest}
      queryName="AutocompleteMessagingRecipientsTypeahead"
      fetchFn={{
        arguments: recipientFilters ? { query: recipientFilters } : {},
        request: getV1MessagingChannelsRecipientsTypeahead,
      }}
      parseFn={(item: Driver_Read_Typeahead) => {
        const parsed = DriverTypeahead.parse(item);

        return { _entity: parsed, label: parsed.fullName, value: parsed.id };
      }}
      renderSecondary={(option, inputValue) => {
        const driverCompany = (
          <DriverOptionSecondary
            companyId={ownCompanyId}
            driver={option._entity}
            query={inputValue}
          />
        );

        if (!option._entity.companyShare) {
          return driverCompany;
        }

        return (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            {driverCompany}
            <Chip
              label={t('messaging.shared_recipient')}
              size="small"
              variant="outlined"
            />
          </Box>
        );
      }}
    />
  );
}
